import chunk from 'lodash/chunk';

import type {
  ListTicketsResponse,
  TicketState,
} from '~/api/k2/typedefs.autogen';
import type { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import type { RequireOnlyOne } from '~/eds/src/types/UtilityTypes';
import type { EntityType } from '~/enums/EntityType';
import type { EsignatureType } from '~/enums/EsignatureType';
import type { TicketActivityType } from '~/enums/TicketActivityType';
import type {
  EsignatureCCRecipient,
  EsignatureEnvelope,
  EsignatureEnvelopeStatus,
  EsignatureRecipient,
  EsignatureSignerStatus,
} from '~/features/ticket/types/esignature';
import type { FullTicket } from '~/features/ticket/types/ticket';
import type { WellKnownFields } from '~/features/ticket/types/well-known-fields';
import type {
  Route,
  RoutingConfiguration,
  RoutingConfigurationPatchInput,
  RoutingInput,
  RoutingSearchResult,
  RoutingUpdateInput,
  UnroutedWorkflow,
} from '~/features/workflow-routing/types';
import type { IntakeForm } from '~/redux/api/methods/ticket';
import type {
  Company,
  CompanyListResponse,
  ShareSettings,
  ShareSettingsPayload,
  Supplier,
  SupplierListResponse,
} from '~/redux/api/methods/workflows/types';
import type { GetSupplierCompanyResponse } from '~/redux/api/methods/wss/getSupplierCompany';
import type { SupplierNegotiationPayload } from '~/redux/api/methods/wss/postTicketSupplierNegotiation';
import type {
  PermissionEntityType,
  PermissionResponse,
  Permissions,
  UserPermissionMapType,
} from '~/redux/types/permissions';
import type { ServiceRequestConfig } from '~/services/createService';
import type { ActivityLogEvent } from '~/types/ActivityLog';
import type { Entity } from '~/types/Entity';
import type { Nullable } from '~/types/Nullable';
import type { PilotId } from '~/types/PilotId';
import type {
  TicketDocument,
  TicketDocumentVersion,
  TicketStage,
  TicketSummary,
} from '~/types/Ticket';
import type { TurnTrackingHistoryType } from '~/types/TurnTracking';
import type { Uuid } from '~/types/Uuid';
import type { Workflow, WorkflowContractType } from '~/types/Workflow';

import { endpoints as autogenEnhancements } from '~/api/everest/enhancements.autogen';
import { Tags } from '~/api/everest/tags.autogen';
import { stampSfdtFileCommentTimezoneOffset } from '~/components/Shared/DocumentEditor/stampSfdtCommentTimezoneOffset';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { checkUsersPermissions as checkUsersPermissionsPilot } from '~/redux/api/methods/client/checkUsersPermissions';
import { computeVisibility } from '~/redux/api/methods/workflows/utils';
import { camelizeKeys } from '~/redux/api/transformers/utils';
import { k2 } from '~/services/serviceInstances';

import { mapEverestError } from './mapError';
import { everestApiRoot } from './root';

// Apply the schema-derived providesTags / invalidatesTags for all generated
// endpoints that are not explicitly overridden below.
everestApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

const K2_COMPLETED_STATE: TicketState = 'COMPLETED';

// `camelizeKeys` produces camelCase attribute keys at runtime, but the
// shared `WorkflowContractType` type still declares them snake_case — a
// pre-existing mismatch between that type and reality, not something to fix
// here. This local type reflects what actually comes back from the endpoint.
type CamelizedWorkflowContractType = {
  id: string;
  type: 'contract_type';
  attributes: {
    id: string;
    linkedWorkflowId?: string;
    descriptor: string;
  };
};

type ServerRoute = {
  id: string;
  workflowId: string;
  routeName: string;
  status: 'active' | 'inactive';
  instructions: string;
  createdDate: string;
  modifiedDate: string;
  createdById: string | null;
  modifiedById: string | null;
  versionNumber: number;
  createdByName?: string | null;
  modifiedByName?: string | null;
  publishedWorkflowId?: string | null;
};

// The individual routing-entry endpoints (create/get/update) return the
// entry directly, unlike search/list/config, which wrap it in `data` — this
// matches the legacy hand-written methods' behavior, not the (stale) OpenAPI
// schema's `RoutingResponse { data: Routing }` shape.
const mapRoute = (route: ServerRoute): Route => ({
  id: route.id,
  workflowId: route.workflowId,
  routeName: route.routeName,
  status: route.status,
  instructions: route.instructions,
  createdDate: route.createdDate,
  modifiedDate: route.modifiedDate,
  createdById: route.createdById,
  modifiedById: route.modifiedById,
  versionNumber: route.versionNumber,
  createdByName: route.createdByName ?? null,
  modifiedByName: route.modifiedByName ?? null,
  publishedWorkflowId: route.publishedWorkflowId ?? null,
});

const ROUTING_LIST_TAG = { type: Tags.routing, id: 'LIST' } as const;
const ROUTING_CONFIGURATION_TAG = {
  type: Tags.routing_configuration,
  id: 'LIST',
} as const;

/**
 * Consumer-facing everest endpoints. These override the raw generated
 * endpoints (`overrideExisting: true`) to reproduce the exact runtime
 * behavior of the legacy hand-written `src/redux/api/methods/routing/*`
 * functions: the same friendly argument and result shapes, the same response
 * mapping, and no automatic error toast (consumers already surface their own
 * error/success toasts around these calls).
 */
export const injected = everestApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    createRouting: build.mutation<Route, { routingInput: RoutingInput }>({
      query: ({ routingInput }) => ({
        url: '/routing',
        method: 'post',
        body: routingInput,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: ServerRoute) => mapRoute(response),
      invalidatesTags: [ROUTING_LIST_TAG],
    }),

    searchRouting: build.query<
      RoutingSearchResult,
      {
        pageSize?: number;
        currentPage?: number;
        searchString?: string;
        orderBy?: string;
      }
    >({
      query: (params) => ({
        url: '/routing/search',
        params,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: {
        data: ServerRoute[];
        totalRecords: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      }) => ({
        data: response.data.map(mapRoute),
        totalRecords: response.totalRecords,
        totalPages: response.totalPages,
        currentPage: response.currentPage,
        pageSize: response.pageSize,
      }),
      providesTags: [ROUTING_LIST_TAG],
    }),

    getRoutingById: build.query<Route, { routingId: string }>({
      query: ({ routingId }) => ({
        url: `/routing/${routingId}`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: ServerRoute) => mapRoute(response),
      providesTags: (_result, _error, { routingId }) => [
        { type: Tags.routing, id: routingId },
      ],
    }),

    updateRouting: build.mutation<
      Route,
      { routingId: string; routingUpdateInput: RoutingUpdateInput }
    >({
      query: ({ routingId, routingUpdateInput }) => ({
        url: `/routing/${routingId}`,
        method: 'put',
        body: routingUpdateInput,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: ServerRoute) => mapRoute(response),
      invalidatesTags: (_result, _error, { routingId }) => [
        { type: Tags.routing, id: routingId },
        ROUTING_LIST_TAG,
      ],
    }),

    deleteRouting: build.mutation<void, { routingId: string }>({
      query: ({ routingId }) => ({
        url: `/routing/${routingId}`,
        method: 'delete',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: (_result, _error, { routingId }) => [
        { type: Tags.routing, id: routingId },
        ROUTING_LIST_TAG,
      ],
    }),

    getUnroutedWorkflows: build.query<UnroutedWorkflow[], void>({
      query: () => ({
        url: '/routing/unrouted-workflows',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: { data: UnroutedWorkflow[] }) =>
        response.data.map((workflow) => ({
          workflowId: workflow.workflowId,
          name: workflow.name,
          description: workflow.description,
        })),
      providesTags: [ROUTING_LIST_TAG],
    }),

    getRoutingConfiguration: build.query<Nullable<RoutingConfiguration>, void>({
      query: () => ({
        url: '/routing-configuration',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: { data?: RoutingConfiguration } = {}) =>
        response.data ?? null,
      providesTags: [ROUTING_CONFIGURATION_TAG],
    }),

    patchRoutingConfiguration: build.mutation<
      RoutingConfiguration,
      { configInput: RoutingConfigurationPatchInput }
    >({
      query: ({ configInput }) => ({
        url: '/routing-configuration',
        method: 'patch',
        body: configInput,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: { data: RoutingConfiguration }) =>
        response.data,
      invalidatesTags: [ROUTING_CONFIGURATION_TAG],
    }),

    // --- Ticket e-signature (packages) ---
    // Tag ids are namespaced per concern (envelope/recipients/activities)
    // since the old code used three distinct TagTypes (EsignatureEnvelope,
    // TicketRecipients, EsignatureActivities) all keyed by ticketId; a single
    // packages-tag id per ticket would cross-invalidate them.
    assignSigner: build.mutation<
      void,
      { ticketId: Uuid; recipientId: Uuid; userId: PilotId }
    >({
      query: ({ ticketId, recipientId, userId }) => ({
        url: `tickets/${ticketId}/packages/reassign-initial-signer`,
        method: 'patch',
        body: { recipientId, newSignerUserId: userId },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEverestError,
      invalidatesTags: (_result, _error, { ticketId }) => [
        { type: Tags.packages, id: `recipients:${ticketId}` },
      ],
    }),

    createEnvelope: build.query<
      { redirectUrl: string },
      { ticketId: Uuid; returnUrl: string; vendor: EsignatureType }
    >({
      query: ({ ticketId, returnUrl, vendor }) => ({
        url: `/tickets/${ticketId}/packages`,
        method: 'post',
        body: { returnUrl, vendor: vendor.toUpperCase() },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: { links: { sender_view: string } }) => ({
        redirectUrl: response.links.sender_view,
      }),
      transformErrorResponse: mapEverestError,
    }),

    createMultiTicketBundle: build.mutation<
      { id: string; links: { sender_view: string }; type: string },
      {
        ticket_ids: string[];
        vendor: EsignatureType;
        return_url: string;
        rtkQueryOptions?: ServiceRequestConfig;
      }
    >({
      query: ({ ticket_ids, vendor, return_url, rtkQueryOptions }) => ({
        url: '/packages/multi-ticket/',
        method: 'post',
        body: {
          ticket_ids,
          vendor: vendor.toUpperCase(),
          return_url,
        },
        rtkQueryOptions: { showErrorToast: true, ...rtkQueryOptions },
      }),
    }),

    getEnvelope: build.query<Nullable<EsignatureEnvelope>, { ticketId: Uuid }>({
      queryFn: async ({ ticketId }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `tickets/${ticketId}/packages/status`,
          rtkQueryOptions: { showErrorToast: false },
        });

        if (result.error) {
          const { status } = result.error as { status?: number | string };
          if (status === 404) {
            return { data: null };
          }
          return { error: mapEverestError(result.error) };
        }

        const response = (result.data ?? { data: null }) as {
          data: {
            type: string;
            id: string;
            attributes: { status: EsignatureEnvelopeStatus };
          } | null;
        };
        if (
          response.data === null ||
          response.data.attributes.status === null
        ) {
          return { data: null };
        }
        return { data: { status: response.data.attributes.status } };
      },
      providesTags: (_result, _error, { ticketId }) => [
        { type: Tags.packages, id: `envelope:${ticketId}` },
      ],
    }),

    getEsignatureActivities: build.query<
      Nullable<{
        activities: Array<{
          message: string;
          date: Date;
          userName: string | null;
        }>;
      }>,
      { ticketId: Uuid }
    >({
      queryFn: async ({ ticketId }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `tickets/${ticketId}/packages/activities`,
          rtkQueryOptions: { showErrorToast: false },
        });

        if (result.error) {
          const { status } = result.error as { status?: number | string };
          if (status === 404) {
            return { data: null };
          }
          return { error: mapEverestError(result.error) };
        }

        const response = (result.data ?? { data: null }) as {
          data: {
            attributes: {
              activities: Array<{
                message: string;
                timestamp: string;
                username: string | null;
              }>;
            };
          } | null;
        };
        if (response.data === null) {
          return { data: null };
        }
        return {
          data: {
            activities: response.data.attributes.activities.map((activity) => ({
              message: activity.message,
              //l10n-ignore: API parsing no formatting required
              date: new Date(activity.timestamp),
              userName: activity.username,
            })),
          },
        };
      },
      providesTags: (_result, _error, { ticketId }) => [
        { type: Tags.packages, id: `activities:${ticketId}` },
      ],
    }),

    getRecipients: build.query<
      { signers: EsignatureRecipient[]; carbonCopies: EsignatureCCRecipient[] },
      { ticketId: Uuid }
    >({
      query: ({ ticketId }) => ({
        url: `tickets/${ticketId}/packages/recipients`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (
        response: {
          data: {
            attributes: {
              recipients: {
                signers: {
                  name: string | null;
                  email: string | null;
                  recipient_id: string;
                  pilot_user_id: PilotId | null;
                  status: EsignatureSignerStatus;
                  is_external: boolean;
                }[];
                carbon_copies: {
                  name: string;
                  email: string;
                  recipient_id: string;
                }[];
              };
            };
          } | null;
        } = { data: null },
      ) => {
        if (response.data === null) {
          return { signers: [], carbonCopies: [] };
        }
        return {
          signers:
            response.data.attributes.recipients.signers?.map((recipient) => ({
              isExternal: recipient.is_external,
              id: recipient.recipient_id,
              userId: recipient.pilot_user_id,
              status: recipient.status,
              external: recipient.email
                ? { name: recipient.name ?? '', email: recipient.email ?? '' }
                : null,
            })) ?? [],
          carbonCopies:
            response.data.attributes.recipients.carbon_copies?.map(
              (recipient) => ({
                id: recipient.recipient_id,
                name: recipient.name,
                email: recipient.email,
              }),
            ) ?? [],
        };
      },
      transformErrorResponse: mapEverestError,
      providesTags: (_result, _error, { ticketId }) => [
        { type: Tags.packages, id: `recipients:${ticketId}` },
      ],
    }),

    sendReminder: build.mutation<
      void,
      { ticketId: Uuid; recipientId: Uuid; vendor: EsignatureType }
    >({
      query: ({ ticketId, recipientId, vendor }) => ({
        url: `tickets/${ticketId}/packages/remind`,
        method: 'post',
        body: { vendor: vendor.toUpperCase(), recipientId },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEverestError,
      invalidatesTags: (_result, _error, { ticketId }) => [
        { type: Tags.packages, id: `recipients:${ticketId}` },
      ],
    }),

    signDocument: build.query<
      { redirectUrl: string },
      {
        ticketId: Uuid;
        recipientId: Uuid;
        returnUrl: string;
        vendor: EsignatureType;
      }
    >({
      query: ({ ticketId, recipientId, returnUrl, vendor }) => ({
        url: `tickets/${ticketId}/packages/sign`,
        method: 'post',
        body: {
          vendor: vendor.toUpperCase(),
          returnUrl,
          recipientId,
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: { links: { signer_view: string } }) => ({
        redirectUrl: response.links.signer_view,
      }),
      transformErrorResponse: mapEverestError,
    }),

    validateMultiTicketBundle: build.mutation<
      {
        is_valid: boolean;
        message?: string;
        tickets: {
          ticket_id: string;
          is_valid: boolean;
          reason?: string;
          message?: string;
        }[];
      },
      {
        ticket_ids: string[];
        vendor?: EsignatureType;
        rtkQueryOptions?: ServiceRequestConfig;
      }
    >({
      query: ({ ticket_ids, vendor, rtkQueryOptions }) => ({
        url: '/packages/multi-ticket/validate',
        method: 'post',
        body: {
          ticket_ids,
          ...(vendor && { vendor: vendor.toUpperCase() }),
        },
        rtkQueryOptions: { showErrorToast: true, ...rtkQueryOptions },
      }),
    }),

    viewEnvelope: build.query<
      { redirectUrl: string },
      {
        ticketId: Uuid;
        vendor: EsignatureType;
        returnUrl: string;
        viewMode?: 'correct';
      }
    >({
      query: ({ ticketId, vendor, returnUrl, viewMode }) => ({
        url: `/tickets/${ticketId}/packages/view`,
        method: 'post',
        body: {
          vendor: vendor.toUpperCase(),
          returnUrl,
          ...(viewMode ? { viewMode } : {}),
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: { links: { console_view: string } }) => ({
        redirectUrl: response.links.console_view,
      }),
      transformErrorResponse: mapEverestError,
    }),

    // --- Ticket ---
    cancelTicket: build.mutation<
      { cancelledDate?: string; esigErrorMessage?: string },
      { ticketId: Uuid; note?: string }
    >({
      query: ({ ticketId, note }) => ({
        url: `tickets/${ticketId}/cancel/`,
        method: 'post',
        body: { reason: note },
        rtkQueryOptions: { showErrorToast: false },
      }),
    }),

    completeTicket: build.mutation<{ data: any }, { ticketId: Uuid }>({
      query: ({ ticketId }) => ({
        url: `/tickets/${ticketId}/complete/`,
        method: 'put',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [{ type: Tags.tickets, id: 'TICKET_SUMMARY' }],
    }),

    getTicketDocument: build.query<TicketDocument, { ticketId: Uuid }>({
      query: ({ ticketId }) => ({
        url: `tickets/${ticketId}/document`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      providesTags: [{ type: Tags.tickets, id: 'CURRENT_VERSION' }],
    }),

    getTicketCurrentStage: build.query<TicketStage, { ticketId: Uuid }>({
      query: ({ ticketId }) => ({
        url: `tickets/${ticketId}/current-stage`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      providesTags: [{ type: Tags.tickets, id: 'CURRENT_STAGE' }],
    }),

    getTicketSummary: build.query<TicketSummary, { ticketId: Uuid }>({
      query: ({ ticketId }) => ({
        url: `tickets/${ticketId}/summary`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      providesTags: [{ type: Tags.tickets, id: 'TICKET_SUMMARY' }],
    }),

    getActivities: build.query<
      ActivityLogEvent[],
      { entityId: Uuid; entityType: EntityType }
    >({
      query: ({ entityId, entityType }) => ({
        url: 'activities',
        params: { entityId, entityType },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: ActivityLogEvent[]) =>
        response.map((activity) => ({
          ...activity,
          //l10n-ignore: API parsing no formatting required
          modifiedDate: new Date(activity.modifiedDate),
        })),
      providesTags: [{ type: Tags.tickets, id: 'ACTIVITY_LOG' }],
    }),

    createActivityLogEvent: build.mutation<
      unknown,
      {
        documentId: Uuid;
        isGenerated: boolean;
        referenceVersionId: Nullable<Uuid>;
        targetVersionId: Nullable<Uuid>;
        ticketId: Uuid;
        type: TicketActivityType;
        versionId: Uuid;
        versionNumber: Nullable<number>;
      }
    >({
      query: (data) => ({
        url: '/activities/',
        method: 'post',
        body: data,
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [{ type: Tags.tickets, id: 'ACTIVITY_LOG' }],
    }),

    bumpVersion: build.mutation<
      TicketDocumentVersion,
      { documentId: Uuid; versionId: Uuid }
    >({
      query: ({ documentId, versionId }) => ({
        url: `/documents/${documentId}/versions/${versionId}/bump-version/`,
        method: 'post',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [{ type: Tags.tickets, id: 'TICKET_SUMMARY' }],
    }),

    addTicketParticipants: build.mutation<
      unknown,
      {
        ticketId: Uuid;
        type: 'user' | 'department';
      } & RequireOnlyOne<{ userIds: PilotId[]; typeIds: PilotId[] }>
    >({
      query: ({ ticketId, type, userIds, typeIds }) => ({
        url: `/tickets/${ticketId}/participants`,
        method: 'post',
        body: {
          ...(userIds && { userIds }),
          ...(typeIds && { typeIds }),
          type,
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [
        { type: Tags.tickets, id: 'PARTICIPANTS' },
        { type: Tags.permissions, id: 'LIST' },
      ],
    }),

    getTicketParticipants: build.query<
      {
        data: Array<{
          role: 'participant' | 'assignee';
          userId: EdsPilotId;
        }>;
      },
      { ticketId: Uuid }
    >({
      query: ({ ticketId }) => ({
        url: `/tickets/${ticketId}/participants/`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      providesTags: [{ type: Tags.tickets, id: 'PARTICIPANTS' }],
    }),

    getTicketIntakeForm: build.query<IntakeForm, Uuid>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/intake-form`,
        rtkQueryOptions: { showErrorToast: false },
      }),
    }),

    getTicketWellKnownFields: build.query<WellKnownFields, Uuid>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/well-known-fields`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: {
        fields: Record<
          string,
          {
            name: string;
            type: WorkflowFieldType;
            value:
              | { value: string | number }
              | { default_value: string; resource_id: string }
              | null;
          }
        >;
      }) => {
        const fields = Object.entries(response.fields).reduce(
          (allFields, [key, field]) => {
            let value: string | number | null = null;
            if (field.value !== null) {
              if (
                field.type === WorkflowFieldType.Reference &&
                'default_value' in field.value
              ) {
                value = field.value.default_value;
              } else if ('value' in field.value) {
                value = field.value.value;
              }
            }
            allFields[key] = { name: field.name, type: field.type, value };
            return allFields;
          },
          {} as WellKnownFields['fields'],
        );
        return { fields };
      },
    }),

    postTicketNextStage: build.mutation<{ data: Object[] }, Uuid>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/next-stage`,
        method: 'post',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [
        { type: Tags.tickets, id: 'TICKET_SUMMARY' },
        { type: Tags.tickets, id: 'CURRENT_VERSION' },
      ],
    }),

    putTicket: build.mutation<
      FullTicket,
      {
        ticketId: Uuid;
        fields: Array<{
          fieldId: Uuid;
          value: { value: string | number | boolean | null };
        }>;
      }
    >({
      query: ({ ticketId, fields }) => ({
        url: `/tickets/${ticketId}`,
        method: 'put',
        body: { fields },
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: (_result, _error, { ticketId }) => [
        { type: Tags.tickets, id: 'TICKET_SUMMARY' },
        { type: Tags.packages, id: `recipients:${ticketId}` },
      ],
    }),

    // --- Ticket document version ---
    getTicketDocumentVersionEditorKey: build.query<
      { key: string },
      { documentId: Uuid; versionId: Uuid }
    >({
      query: ({ documentId, versionId }) => ({
        url: `documents/${documentId}/versions/${versionId}/editor-key/`,
        rtkQueryOptions: { showErrorToast: false },
      }),
    }),

    downloadTicketDocumentVersionEmailAttachment: build.query<
      { url: string },
      { documentId: Uuid; versionId: Uuid }
    >({
      query: ({ documentId, versionId }) => ({
        url: `documents/${documentId}/versions/${versionId}/download-email-attachment/`,
        rtkQueryOptions: { showErrorToast: false },
      }),
    }),

    convertPdfToDocx: build.mutation<
      unknown,
      { documentId: Uuid; versionId: Uuid }
    >({
      query: ({ documentId, versionId }) => ({
        url: `/documents/${documentId}/versions/${versionId}/convert-to-docx`,
        method: 'post',
        rtkQueryOptions: { showErrorToast: false },
      }),
    }),

    // --- Turn tracking ---
    getTurnTrackingHistory: build.query<
      TurnTrackingHistoryType[],
      { ticketId: Uuid }
    >({
      query: ({ ticketId }) => ({
        url: `tickets/${ticketId}/turn-trackings`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (
        response: {
          id: Uuid;
          start_time: Date;
          end_time: Nullable<Date>;
          party_id: Uuid;
        }[],
      ) =>
        response.map((entry) => ({
          turnId: entry.id,
          startTime: new Date(entry.start_time),
          endTime: entry?.end_time ? new Date(entry.end_time) : null,
          partyId: entry.party_id,
        })),
      providesTags: [{ type: Tags.turn_tracking, id: 'LIST' }],
    }),

    updateTurnTrackingEntry: build.mutation<
      unknown,
      {
        ticketId: Uuid;
        entry: TurnTrackingHistoryType;
      }
    >({
      query: ({ ticketId, entry }) => ({
        url: `tickets/${ticketId}/turn-trackings/${entry.turnId}`,
        method: 'patch',
        body: {
          start_time: entry.startTime,
          end_time: entry.endTime,
          party_id: entry.partyId,
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [{ type: Tags.turn_tracking, id: 'LIST' }],
    }),

    createTurnTrackingEntry: build.mutation<
      unknown,
      { ticketId: Uuid; partyId: Uuid }
    >({
      query: ({ ticketId, partyId }) => ({
        url: `tickets/${ticketId}/turn-trackings`,
        method: 'post',
        body: { party_id: partyId },
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [{ type: Tags.turn_tracking, id: 'LIST' }],
    }),

    // --- WSS / supplier ---
    getSupplierCompany: build.query<
      GetSupplierCompanyResponse,
      { supplierId: string; includeContacts?: boolean }
    >({
      query: ({ supplierId, includeContacts = true }) => ({
        url: `/suppliers/${supplierId}`,
        params: { include_contacts: JSON.stringify(includeContacts) },
        rtkQueryOptions: { showErrorToast: false },
      }),
    }),

    postTicketSupplierNegotiation: build.mutation<
      { data: { status: string; action: string } },
      { ticketId: Uuid; payload: SupplierNegotiationPayload }
    >({
      query: ({ ticketId, payload }) => ({
        url: `/tickets/${ticketId}/supplier-negotiation`,
        method: 'post',
        body: payload,
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [
        { type: Tags.tickets, id: 'TICKET_SUMMARY' },
        { type: Tags.tickets, id: 'ACTIVITY_LOG' },
      ],
    }),

    resolveWssTicketId: build.query<
      { ticketId: Uuid; isCompleted: boolean } | null,
      {
        contractRecordUniqueId?: string;
        wssContractId?: Uuid;
        useK2?: boolean;
      }
    >({
      queryFn: async (
        { contractRecordUniqueId, wssContractId, useK2 },
        _api,
        _extra,
        baseQuery,
      ) => {
        if (useK2 && contractRecordUniqueId) {
          const response: ListTicketsResponse = await k2.get('/tickets', {
            params: { contract_record_unique_id: contractRecordUniqueId },
          });
          const ticket = response.data?.[0];
          if (!ticket) {
            return { data: null };
          }
          return {
            data: {
              ticketId: ticket.id,
              isCompleted: ticket.attributes.state === K2_COMPLETED_STATE,
            },
          };
        }

        const result = await baseQuery({
          url: '/tickets/resolve-ticket-id/',
          params: {
            wss_contract_id: wssContractId,
            contract_record_unique_id: contractRecordUniqueId,
          },
          rtkQueryOptions: { showErrorToast: false },
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as {
          ticket_id: Uuid;
          is_completed: boolean;
        };
        return {
          data: {
            ticketId: response.ticket_id,
            isCompleted: response.is_completed,
          },
        };
      },
    }),

    // --- ACL / permissions ---
    getEntityPermissions: build.query<
      Permissions,
      {
        userIds: PilotId[];
        entity: Entity<PermissionEntityType>;
        enableChunkSpliting?: boolean;
      }
    >({
      queryFn: async (
        { userIds, entity, enableChunkSpliting = false },
        _api,
        _extra,
        baseQuery,
      ) => {
        let permissions: PermissionResponse[] = [];

        if (entity.type === 'ticket') {
          const CHUNK_SIZE = 1000;
          const userIdsChunks = enableChunkSpliting
            ? chunk(userIds, CHUNK_SIZE)
            : [userIds];
          const results = await Promise.all(
            userIdsChunks.map(async (userIdsChunk) => {
              const result = await baseQuery({
                url: '/permissions/check-user-accessibility/',
                method: 'post',
                body: {
                  entityType: entity.type,
                  entityId: entity.id,
                  userIds: userIdsChunk,
                },
                rtkQueryOptions: { showErrorToast: false },
              });
              if (result.error) {
                throw result.error;
              }
              return result.data as PermissionResponse;
            }),
          );
          permissions = results;
        } else {
          permissions = await checkUsersPermissionsPilot({
            userIds,
            entity: entity as Entity<'document' | 'document_handler'>,
            enableChunkSpliting,
          });
        }

        const mergedPermissions =
          permissions?.reduce(
            (prev, current) => ({ ...prev, ...current.user_permission_map }),
            {},
          ) || {};

        return {
          data: {
            entity: { type: entity.type, id: entity.id },
            userPermissionMap: camelizeKeys(
              mergedPermissions,
            ) as unknown as UserPermissionMapType,
          },
        };
      },
      providesTags: [{ type: Tags.permissions, id: 'LIST' }],
    }),

    // --- Workflows ---
    deleteWorkflow: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/workflows/${id}`,
        method: 'delete',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [
        { type: Tags.workflows, id: 'LIST' },
        { type: Tags.routing, id: 'LIST' },
      ],
    }),

    duplicateWorkflow: build.mutation<
      unknown,
      { id: string; targetWorkflowId?: string }
    >({
      query: ({ id, targetWorkflowId }) => ({
        url: targetWorkflowId
          ? `/workflows/${id}/duplicate?targetWorkflowId=${targetWorkflowId}`
          : `/workflows/${id}/duplicate`,
        method: 'post',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: [{ type: Tags.workflows, id: 'LIST' }],
    }),

    getAmendIntakeForm: build.query<
      unknown,
      { contractRecordUniqueId: string }
    >({
      query: ({ contractRecordUniqueId }) => ({
        url: `/integrate-workflow/amend/${contractRecordUniqueId}/intake-form`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEverestError,
    }),

    getIntegrateWorkflowCompanies: build.query<
      Company[],
      { searchString: string }
    >({
      query: ({ searchString }) => ({
        url: `/companies?q=${encodeURIComponent(searchString)}`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: CompanyListResponse) =>
        response.data.map((company) => ({
          ...camelizeKeys(company.attributes),
          id: company.id,
        })),
      transformErrorResponse: mapEverestError,
    }),

    getIntegrateWorkflowIntakeForm: build.query<
      unknown,
      { id: string; referenceId?: string }
    >({
      query: ({ id, referenceId }) => ({
        url: `/integrate-workflow/${id}/intake-form/link/${referenceId ? `?reference_id=${referenceId}` : ''}`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEverestError,
    }),

    getIntegrateWorkflowSuppliers: build.query<
      Supplier[],
      { workflowId: string; searchString?: string }
    >({
      query: ({ workflowId, searchString }) => ({
        url: `/integrate-workflow/${workflowId}/suppliers${searchString ? `?q=${encodeURIComponent(searchString)}` : ''}`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: SupplierListResponse) =>
        response.data.map((supplier) => ({
          ...camelizeKeys(supplier.attributes),
          id: supplier.id,
        })),
      transformErrorResponse: mapEverestError,
    }),

    getWorkflow: build.query<Workflow, { workflowId: string }>({
      query: ({ workflowId }) => ({
        url: `/workflows/${workflowId}`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (workflow: any) => ({
        ...workflow,
        isDraft: workflow.status === 'draft',
        isPublished: workflow.published,
        canPublish: workflow.publishable,
        lastModifiedBy: workflow.lastModifiedById,
      }),
      providesTags: (_result, _error, { workflowId }) => [
        { type: Tags.workflows, id: workflowId },
      ],
    }),

    getWorkflows: build.query<Workflow[], void>({
      query: () => ({
        url: '/workflows/',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (workflows: any[]) =>
        workflows.map((workflow) => ({
          ...workflow,
          isDraft: workflow.status === 'draft',
          isPublished: workflow.published,
          canPublish: workflow.publishable,
          lastModifiedBy: workflow.lastModifiedById,
        })),
      providesTags: [{ type: Tags.workflows, id: 'LIST' }],
    }),

    getWorkflowContractTypes: build.query<
      CamelizedWorkflowContractType[],
      void
    >({
      query: () => ({
        url: '/contract-types',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: { data: WorkflowContractType[] }) =>
        response.data.map(
          (item) => camelizeKeys(item) as CamelizedWorkflowContractType,
        ),
    }),

    getWorkflowShareSettings: build.query<
      {
        hasSettings: boolean;
        visibility: string;
        userIds: number[];
        departmentIds: number[];
      },
      { workflowId: string; currentUserId: number }
    >({
      queryFn: async (
        { workflowId, currentUserId },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/workflows/${workflowId}/visibility-settings/`,
          rtkQueryOptions: { showErrorToast: false },
        });

        if (result.error) {
          const { status } = result.error as { status?: number | string };
          if (status === 404) {
            return {
              data: {
                hasSettings: false,
                visibility: 'OPEN',
                userIds: [],
                departmentIds: [],
              },
            };
          }
          return { error: result.error };
        }

        const response = result.data as ShareSettingsPayload;
        const userIds = response.data.attributes.user_ids;
        const departmentIds = response.data.attributes.department_ids;
        return {
          data: {
            hasSettings: true,
            visibility: computeVisibility(
              userIds,
              departmentIds,
              currentUserId,
            ),
            userIds,
            departmentIds,
          },
        };
      },
      providesTags: (_result, _error, { workflowId }) => [
        { type: Tags.workflows, id: `share-settings:${workflowId}` },
      ],
    }),

    publishWorkflow: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/workflows/${id}/publish`,
        method: 'patch',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: Tags.workflows, id },
        { type: Tags.workflows, id: 'LIST' },
        { type: Tags.routing, id: 'LIST' },
      ],
    }),

    unpublishWorkflow: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/workflows/${id}/unpublish`,
        method: 'patch',
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: Tags.workflows, id },
        { type: Tags.workflows, id: 'LIST' },
        { type: Tags.routing, id: 'LIST' },
      ],
    }),

    updateWorkflowShareSettings: build.mutation<
      unknown,
      {
        workflowId: string;
        shareSettings: ShareSettings;
        hasSettings: boolean;
        currentUserId: number;
      }
    >({
      query: ({ workflowId, shareSettings, hasSettings }) => ({
        url: `/workflows/${workflowId}/visibility-settings/`,
        method: hasSettings ? 'put' : 'post',
        body: {
          data: {
            type: 'workflow-visibility',
            attributes: {
              user_ids: shareSettings.userIds,
              department_ids: shareSettings.departmentIds,
            },
          },
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      invalidatesTags: (_result, _error, { workflowId }) => [
        { type: Tags.workflows, id: `share-settings:${workflowId}` },
        { type: Tags.workflows, id: 'LIST' },
      ],
    }),

    // --- Client ---
    copyWorkflow: build.mutation<
      unknown,
      {
        sourceWorkflowId: string;
        destinationClientId: number;
        destinationWorkflowId?: string;
      }
    >({
      query: ({
        sourceWorkflowId,
        destinationClientId,
        destinationWorkflowId,
      }) => ({
        url: `workflows/${sourceWorkflowId}/clone`,
        method: 'post',
        body: {
          target_client_id: destinationClientId,
          target_workflow_id: destinationWorkflowId || undefined,
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
    }),

    // --- Document / SFDT ---
    getDocumentSfdt: build.query<object, { documentId: Uuid; versionId: Uuid }>(
      {
        query: ({ documentId, versionId }) => ({
          url: `/documents/${documentId}/versions/${versionId}/download/sfdt`,
          rtkQueryOptions: {
            showErrorToast: false,
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
              Expires: '0',
            },
          },
        }),
      },
    ),

    saveDocumentSfdt: build.mutation<
      unknown,
      {
        documentId: Uuid;
        versionId: Uuid;
        document: File;
        isDoneEditing: boolean;
      }
    >({
      queryFn: async (
        { documentId, versionId, document, isDoneEditing },
        _api,
        _extra,
        baseQuery,
      ) => {
        const formData = new FormData();
        const stampedDocument =
          await stampSfdtFileCommentTimezoneOffset(document);
        formData.append('file', stampedDocument);
        const result = await baseQuery({
          url: `/documents/${documentId}/versions/${versionId}/sfdt`,
          method: 'post',
          body: formData,
          params: { isDoneEditing },
          rtkQueryOptions: { showErrorToast: false },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
    }),

    backupDocumentSfdt: build.mutation<
      unknown,
      { documentId: Uuid; versionId: Uuid; document: File }
    >({
      queryFn: async (
        { documentId, versionId, document },
        _api,
        _extra,
        baseQuery,
      ) => {
        const formData = new FormData();
        const stampedDocument =
          await stampSfdtFileCommentTimezoneOffset(document);
        formData.append('file', stampedDocument);
        formData.append('file_type', 'sfdt');
        const result = await baseQuery({
          url: `/documents/${documentId}/versions/${versionId}/backups`,
          method: 'post',
          body: formData,
          rtkQueryOptions: { showErrorToast: false },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
    }),

    tokenizeRiskContent: build.query<
      unknown,
      { riskId: string; textList: string[]; highlightsIndex?: string }
    >({
      query: ({ riskId, textList, highlightsIndex = '0' }) => {
        const formData = new FormData();
        formData.append('riskId', riskId);
        formData.append('textList', JSON.stringify(textList));
        formData.append('highlightsIndex', highlightsIndex);
        return {
          url: '/activities/tokenize',
          method: 'post',
          body: formData,
          rtkQueryOptions: { showErrorToast: false },
        };
      },
    }),

    compareDocumentsSfdt: build.query<
      unknown,
      { referenceVersionId: Uuid; targetVersionId: Uuid; documentId: Uuid }
    >({
      query: ({ referenceVersionId, targetVersionId, documentId }) => ({
        url: `/documents/${documentId}/versions/compare`,
        method: 'post',
        body: { referenceVersionId, targetVersionId },
        rtkQueryOptions: {
          showErrorToast: false,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      }),
    }),
  }),
});
